import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { SectionHeading } from '../components/shared/SectionHeading';
import { GrowthComparisonChart } from '../components/shared/GrowthComparisonChart';
import { advertiserBenefits } from '../data/mock';
import * as Icons from 'lucide-react';
import { ArrowRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

export default function Advertisers() {
  const { t } = useLanguage();
  
  const howItWorksSteps = [
    {
      step: '1',
      title: t.partnersPage.howItWorks.steps[0].title,
      desc: t.partnersPage.howItWorks.steps[0].desc,
      icon: 'Target'
    },
    {
      step: '2',
      title: t.partnersPage.howItWorks.steps[1].title,
      desc: t.partnersPage.howItWorks.steps[1].desc,
      icon: 'Settings'
    },
    {
      step: '3',
      title: t.partnersPage.howItWorks.steps[2].title,
      desc: t.partnersPage.howItWorks.steps[2].desc,
      icon: 'Zap'
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-50 via-white to-slate-50 dark:from-emerald-950/20 dark:via-slate-900 dark:to-slate-900" />
        <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl" />
        
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-400 text-sm font-medium mb-8">
                {t.partnersPage.badge}
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 dark:text-white tracking-tight leading-tight mb-6">
                {t.partnersPage.title}{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-emerald-500">
                  {t.partnersPage.titleHighlight}
                </span>
              </h1>
              <p className="text-xl text-slate-600 dark:text-slate-400 mb-8">
                {t.partnersPage.subtitle}
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/contact">
                  <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700 text-white shadow-xl shadow-emerald-600/25 h-14 px-8 text-base">
                    {t.partnersPage.requestLeadFlow}
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <Link to="/services">
                  <Button size="lg" variant="outline" className="h-14 px-8 text-base border-slate-300 dark:border-slate-600">
                    {t.partnersPage.viewServices}
                  </Button>
                </Link>
              </div>
            </div>
            
            <div className="bg-white dark:bg-slate-800/50 rounded-3xl p-8 shadow-2xl shadow-slate-200/50 dark:shadow-slate-900/50 border border-slate-200 dark:border-slate-700">
              <GrowthComparisonChart />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 bg-white dark:bg-slate-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <SectionHeading
            badge={t.partnersPage.benefits.badge}
            title={t.partnersPage.benefits.title}
            subtitle={t.partnersPage.benefits.subtitle}
            centered
          />
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mt-16">
            {advertiserBenefits.map((benefit, index) => {
              const IconComponent = Icons[benefit.icon] || Icons.Check;
              // Get translated benefit from translation file
              const benefitKey = Object.keys(t.partnersPage.benefits.items)[index];
              const translatedBenefit = t.partnersPage.benefits.items[benefitKey];
              
              return (
                <Card key={index} className="group bg-white dark:bg-slate-800/50 border-slate-200 dark:border-slate-700/50 hover:border-emerald-300 dark:hover:border-emerald-700 hover:shadow-xl transition-all duration-300">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 rounded-xl bg-emerald-50 dark:bg-emerald-900/20 flex items-center justify-center mb-4 group-hover:bg-emerald-100 dark:group-hover:bg-emerald-900/40 transition-colors duration-300">
                      <IconComponent className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
                      {translatedBenefit.title}
                    </h3>
                    <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed">
                      {translatedBenefit.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 bg-slate-50 dark:bg-slate-900/50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <SectionHeading
            badge={t.partnersPage.howItWorks.badge}
            title={t.partnersPage.howItWorks.title}
            subtitle={t.partnersPage.howItWorks.subtitle}
            centered
          />
          
          <div className="grid md:grid-cols-3 gap-8 mt-16">
            {howItWorksSteps.map((item, index) => {
              const IconComponent = Icons[item.icon] || Icons.Check;
              return (
                <div key={index} className="relative text-center">
                  <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center mb-6 shadow-lg shadow-emerald-500/30">
                    <IconComponent className="w-8 h-8 text-white" />
                  </div>
                  <div className="absolute -top-2 -right-2 md:right-1/4 w-8 h-8 rounded-full bg-slate-900 dark:bg-white text-white dark:text-slate-900 flex items-center justify-center text-sm font-bold">
                    {item.step}
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">{item.title}</h3>
                  <p className="text-slate-600 dark:text-slate-400">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ Preview */}
      <section className="py-24 bg-white dark:bg-slate-900">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <SectionHeading
            badge={t.partnersPage.faq.badge}
            title={t.partnersPage.faq.title}
            centered
          />
          
          <div className="mt-12 space-y-4">
            {t.partnersPage.faq.items.map((item, index) => (
              <div key={index} className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-6">
                <h4 className="font-semibold text-slate-900 dark:text-white mb-2">{item.q}</h4>
                <p className="text-slate-600 dark:text-slate-400 text-sm">{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-gradient-to-br from-emerald-600 to-emerald-700">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            {t.partnersPage.ctaTitle}
          </h2>
          <p className="text-xl text-emerald-100 mb-10">
            {t.partnersPage.ctaSubtitle}
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-white text-emerald-700 hover:bg-emerald-50 shadow-xl h-14 px-8 text-base font-semibold">
              {t.partnersPage.requestLeadFlow}
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
